import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useAssignableCoursesQuery } from 'components/hooks/assignableCourseQueries';

import SidePanel from 'components/application/SidePanel';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import RolesForm from 'components/roles/RolesForm';
import RolesShow from 'components/roles/RolesShow';
import FormFooter from 'components/sidepanels/FormFooter';
import ShowFooter from 'components/sidepanels/ShowFooter';
import TrainingRegisterReportsSelector from 'components/trainingRegisterReports/TrainingRegisterReportsSelector';

import { focusField } from 'components/helpers/refs';
import { resourceShape } from 'components/helpers/serialisableResources';

const selectableReportTypes = [
  'expiringTraining',
  'completedTraining',
  'currentTrainingStatus',
];

export default function RolesSidePanel({
  closeSidePanel,
  currentRole,
  domainCourseCompanyRoles,
  isEditDisabled,
  isOpen,
  isSubmitDisabled,
  onCourseCompanyRoleDelete,
  onCourseCompanyRoleRequiredChange,
  onCreateClick,
  onDeleteClick,
  onEditClick,
  onRoleInputChange,
  onRoleOptionChange,
  onUpdateClick,
  removeErrorStyling,
  requestError,
  selectedRole,
  sidePanelContext,
}) {
  const currentActor = useCurrentActor();
  const sidePanelFieldRef = useRef();

  const {
    hasPersonnelViewableAccess,
    hasRoleEditableAccess,
    isMultiDivisionAccount,
  } = useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'roles.RolesSidePanel',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const {
    data: domainAssignableCourses,
    isSuccess: isDomainAssignableCoursesFetchSuccess,
  } = useAssignableCoursesQuery({ queryOptions: { enabled: isOpen } });

  const footerContent = () => {
    switch (sidePanelContext) {
      case 'show':
        return (
          hasRoleEditableAccess &&
          selectedRole && (
            <ShowFooter
              deleteDisabled={selectedRole.attributes.isDeleteProhibited}
              deleteDisabledTooltip={i18nT(
                'show.footer.delete.disabledTooltip',
              )}
              deleteText={i18nTCommon('roles.deleteRole')}
              editDisabled={
                isEditDisabled || !isDomainAssignableCoursesFetchSuccess
              }
              editText={i18nTCommon('roles.editRole')}
              onDeleteClick={onDeleteClick}
              onEditClick={onEditClick}
            />
          )
        );
      case 'edit':
        return (
          <FormFooter
            onCancel={closeSidePanel}
            onSubmit={onUpdateClick}
            submitButtonDisabled={isSubmitDisabled}
            text={i18nTCommon('shared.saveChanges')}
          />
        );
      case 'new':
        return (
          <FormFooter
            onCancel={closeSidePanel}
            onSubmit={onCreateClick}
            submitButtonDisabled={isSubmitDisabled}
            text={i18nTCommon('roles.addRole')}
          />
        );
    }
  };

  const onBackdropClick = () => {
    const panelLocked = ['new', 'edit'].includes(sidePanelContext);
    if (!panelLocked) closeSidePanel();
  };

  const assignableCourses = domainAssignableCourses?.data || [];

  const isReportsTabViewable =
    currentActor.isAllowedFeature('training_register') &&
    hasPersonnelViewableAccess;

  return (
    <SidePanel
      bodyContent={
        sidePanelContext === 'show' ?
          selectedRole && (
            <RolesShow
              assignableCourses={assignableCourses}
              domainCourseCompanyRoles={domainCourseCompanyRoles}
              isSidePanelOpen={isOpen}
              roleId={selectedRole.id}
            />
          )
        : <RolesForm
            assignableCourses={assignableCourses}
            currentRole={currentRole}
            onCourseCompanyRoleDelete={onCourseCompanyRoleDelete}
            onCourseCompanyRoleRequiredChange={
              onCourseCompanyRoleRequiredChange
            }
            onInputChange={onRoleInputChange}
            onOptionChange={onRoleOptionChange}
            removeErrorStyling={removeErrorStyling}
            requestError={requestError}
            sidePanelFieldRef={sidePanelFieldRef}
            submitDisabled={isSubmitDisabled}
          />
      }
      closeCallback={closeSidePanel}
      color='tertiary'
      contentContext={sidePanelContext}
      displayClose={sidePanelContext === 'show'}
      footerContent={footerContent()}
      headerContent={
        <>
          <div className='popup__title popup__title--tertiary'>
            <h1
              className={classNames(
                sidePanelContext === 'show' ?
                  'tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-500'
                : 'tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900',
              )}
            >
              {sidePanelContext === 'edit' ?
                i18nTCommon('roles.editRole')
              : sidePanelContext === 'new' ?
                i18nTCommon('roles.addNewRole')
              : sidePanelContext === 'show' && i18nTCommon('shared.role')}
            </h1>
          </div>
          {sidePanelContext === 'show' && selectedRole && (
            <div>
              <h2
                className={classNames(
                  'tw-mt-2 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-700',
                  isReportsTabViewable ? 'tw-mb-4' : 'tw-mb-0',
                )}
              >
                {selectedRole.attributes.position}
              </h2>
              {isReportsTabViewable && (
                <div>
                  <TrainingRegisterReportsSelector
                    filter={{ roleId: { eq: selectedRole.id } }}
                    selectableReportTypes={selectableReportTypes}
                  />
                </div>
              )}
            </div>
          )}
          {sidePanelContext === 'edit' && isMultiDivisionAccount && (
            <p className='tw-m-0 tw-mt-5 tw-rounded-lg tw-border-0 tw-bg-amber-025 tw-p-3 tw-text-amber-800'>
              {i18nT('edit.header.warning')}
            </p>
          )}
        </>
      }
      isOpen={isOpen}
      onBackdropClick={onBackdropClick}
      onOpen={() => sidePanelContext === 'new' && focusField(sidePanelFieldRef)}
      requestError={requestError}
      scrollHeader={false}
      submitDisabled={isSubmitDisabled}
    />
  );
}

RolesSidePanel.propTypes = {
  closeSidePanel: PropTypes.func.isRequired,
  currentRole: PropTypes.shape({
    courseCompanyRoles: PropTypes.arrayOf(
      PropTypes.shape({
        courseId: PropTypes.string.isRequired,
        required: PropTypes.bool.isRequired,
      }),
    ).isRequired,
    position: PropTypes.string.isRequired,
  }).isRequired,
  domainCourseCompanyRoles: PropTypes.shape({
    data: PropTypes.arrayOf(resourceShape('courseCompanyRole')).isRequired,
  }),
  isEditDisabled: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isSubmitDisabled: PropTypes.bool.isRequired,
  onCourseCompanyRoleDelete: PropTypes.func.isRequired,
  onCourseCompanyRoleRequiredChange: PropTypes.func.isRequired,
  onCreateClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired,
  onRoleInputChange: PropTypes.func.isRequired,
  onRoleOptionChange: PropTypes.func.isRequired,
  onUpdateClick: PropTypes.func.isRequired,
  removeErrorStyling: PropTypes.func.isRequired,
  requestError: PropTypes.object,
  selectedRole: resourceShape('companyRole'),
  sidePanelContext: PropTypes.string.isRequired,
};
